import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface Access {
  id: string;
  userId: string;
  materialId: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  material?: {
    id: string;
    title: string;
    description: string;
    referenceId?: string;
  };
}

interface CreateAccessData {
  userId: string;
  materialId: string;
  expiresAt: string; // Obrigatório: calculado no frontend (100 anos se usar data padrão)
}

interface UpdateAccessData {
  expiresAt?: string;
  materialId?: string;
}

interface AccessManagerState {
  accesses: Access[];
  isLoading: boolean;
  error: string | null;
}

export const useAccessManager = () => {
  const [accessManagerState, setAccessManagerState] = useState<AccessManagerState>({
    accesses: [],
    isLoading: false,
    error: null,
  });

  const { fetchWithAuth } = useFetch();

  // Buscar todos os acessos
  const fetchAllAccesses = useCallback(async (): Promise<Access[]> => {
    setAccessManagerState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetchWithAuth(API_ROUTES.ACCESSES.list());

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar acessos: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const accesses = data.accesses || [];

      setAccessManagerState({
        accesses,
        isLoading: false,
        error: null,
      });

      return accesses;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setAccessManagerState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [fetchWithAuth]);

  // Criar novo acesso
  const createAccess = useCallback(async (accessData: CreateAccessData): Promise<Access> => {
    setAccessManagerState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetchWithAuth(API_ROUTES.ACCESSES.create(), {
        method: 'POST',
        body: JSON.stringify(accessData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao criar acesso: ${response.status} - ${errorText}`);
      }

      // Verificar se a resposta tem conteúdo antes de fazer parse JSON
      const responseText = await response.text();
      let newAccess: Access;
      
      if (responseText.trim()) {
        try {
          newAccess = JSON.parse(responseText);
        } catch (parseError) {
          // Se a resposta não for JSON válido, criar um objeto básico
          newAccess = { 
            id: Date.now().toString(), // ID temporário
            userId: accessData.userId,
            materialId: accessData.materialId,
            expiresAt: accessData.expiresAt,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
        }
      } else {
        // Se a resposta estiver vazia, criar um objeto básico
        newAccess = { 
          id: Date.now().toString(), // ID temporário
          userId: accessData.userId,
          materialId: accessData.materialId,
          expiresAt: accessData.expiresAt,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      setAccessManagerState(prev => ({
        ...prev,
        accesses: [...prev.accesses, newAccess],
        isLoading: false,
        error: null,
      }));

      return newAccess;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setAccessManagerState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [fetchWithAuth]);

  // Atualizar acesso
  const updateAccess = useCallback(async (accessId: string, updateData: UpdateAccessData): Promise<Access> => {
    setAccessManagerState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetchWithAuth(API_ROUTES.ACCESSES.update(accessId), {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao atualizar acesso: ${response.status} - ${errorText}`);
      }

      const updatedAccess = await response.json();
      
      setAccessManagerState(prev => ({
        ...prev,
        accesses: prev.accesses.map(access => 
          access.id === accessId ? updatedAccess : access
        ),
        isLoading: false,
        error: null,
      }));

      return updatedAccess;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setAccessManagerState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [fetchWithAuth]);

  // Deletar acesso
  const deleteAccess = useCallback(async (accessId: string): Promise<void> => {
    setAccessManagerState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetchWithAuth(API_ROUTES.ACCESSES.delete(accessId), {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao deletar acesso: ${response.status} - ${errorText}`);
      }

      setAccessManagerState(prev => ({
        ...prev,
        accesses: prev.accesses.filter(access => access.id !== accessId),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setAccessManagerState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [fetchWithAuth]);

  // Buscar acessos por usuário
  const getUserAccesses = useCallback((userId: string): Access[] => {
    return accessManagerState.accesses.filter(access => access.userId === userId);
  }, [accessManagerState.accesses]);

  return {
    accessManagerState,
    fetchAllAccesses,
    createAccess,
    updateAccess,
    deleteAccess,
    getUserAccesses,
  };
};
